import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import authService from "../services/discordAuth";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";
import "./NotificationsPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

function NotificationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  usePageTitle(t("notifications_page.pageTitle"));

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    unread: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  });

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate("/");
      return;
    }

    loadNotifications();
  }, [navigate, filter]);

  const loadNotifications = async (offset = 0) => {
    try {
      setLoading(true);
      const token = authService.getAccessToken();

      const response = await fetch(`${BACKEND_URL}/api/notifications?limit=20&offset=${offset}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Filtrar notificações se necessário
        let filteredNotifications = data.notifications;
        if (filter === "unread") {
          filteredNotifications = data.notifications.filter((n) => !n.read);
        } else if (filter === "read") {
          filteredNotifications = data.notifications.filter((n) => n.read);
        }

        setNotifications(filteredNotifications);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error("Erro ao carregar notificações:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = authService.getAccessToken();

      const response = await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
        setPagination((prev) => ({
          ...prev,
          unread: Math.max(0, prev.unread - 1),
        }));
      }
    } catch (error) {
      console.error("Erro ao marcar como lida:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = authService.getAccessToken();

      const response = await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        trackEvent("notifications_mark_all_read");
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setPagination((prev) => ({ ...prev, unread: 0 }));
      }
    } catch (error) {
      console.error("Erro ao marcar todas como lidas:", error);
    }
  };

  const handleDelete = (notificationId) => {
    setNotificationToDelete(notificationId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      const token = authService.getAccessToken();

      const response = await fetch(`${BACKEND_URL}/api/notifications/${notificationToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationToDelete));
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
        }));
      }
    } catch (error) {
      console.error("Erro ao deletar notificação:", error);
    } finally {
      setShowDeleteModal(false);
      setNotificationToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setNotificationToDelete(null);
  };

  const handleClearAll = () => {
    setShowClearAllModal(true);
  };

  const confirmClearAll = async () => {
    try {
      const token = authService.getAccessToken();

      const response = await fetch(`${BACKEND_URL}/api/notifications/clear-all`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
          unread: 0,
        }));

        // Mostrar modal de sucesso
        const msg =
          data.deletedCount === 1
            ? t("notifications_page.modals.clear_success_singular", {
                count: data.deletedCount,
              })
            : t("notifications_page.modals.clear_success_plural", {
                count: data.deletedCount,
              });
        setSuccessMessage(msg);
        setShowSuccessModal(true);
      } else {
        // Mostrar modal de erro
        setSuccessMessage(t("notifications_page.modals.clear_error"));
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao excluir todas as notificações:", error);
      // Mostrar modal de erro
      setSuccessMessage(t("notifications_page.modals.clear_error"));
      setShowSuccessModal(true);
    } finally {
      setShowClearAllModal(false);
    }
  };

  const cancelClearAll = () => {
    setShowClearAllModal(false);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      vote: "🗳️",
      review: "⭐",
      bot_approved: "✅",
      bot_rejected: "❌",
      vote_reminder: "⏰",
      system: "🔔",
    };
    return icons[type] || "🔔";
  };

  const getNotificationColor = (type) => {
    const colors = {
      vote: "#8a2be2",
      review: "#ffd700",
      bot_approved: "#00ff00",
      bot_rejected: "#ff4444",
      vote_reminder: "#00bfff",
      system: "#888",
    };
    return colors[type] || "#888";
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="notifications-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t("notifications_page.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="container">
        {/* Header */}
        <div className="notifications-header">
          <div className="header-content">
            <h1>{t("notifications_page.headerTitle")}</h1>
            <p className="header-subtitle">
              {pagination.total === 1
                ? t("notifications_page.stats_singular", {
                    total: pagination.total,
                  })
                : t("notifications_page.stats_plural", {
                    total: pagination.total,
                  })}
              {pagination.unread > 0 && (
                <span className="unread-count">
                  {" "}
                  {pagination.unread === 1
                    ? t("notifications_page.unread_stats_singular", {
                        count: pagination.unread,
                      })
                    : t("notifications_page.unread_stats_plural", {
                        count: pagination.unread,
                      })}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="notifications-filters">
          <div className="filters-left">
            <button className={`filter-btn ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
              {t("notifications_page.filter_all", { count: pagination.total })}
            </button>
            <button className={`filter-btn ${filter === "unread" ? "active" : ""}`} onClick={() => setFilter("unread")}>
              {t("notifications_page.filter_unread", {
                count: pagination.unread,
              })}
            </button>
            <button className={`filter-btn ${filter === "read" ? "active" : ""}`} onClick={() => setFilter("read")}>
              {t("notifications_page.filter_read", {
                count: pagination.total - pagination.unread,
              })}
            </button>
          </div>
          <div className="filters-right">
            {pagination.unread > 0 && (
              <button className="btn btn-primary" onClick={handleMarkAllAsRead}>
                {t("notifications_page.action_mark_all")}
              </button>
            )}
            {pagination.total > 0 && (
              <button className="btn btn-danger btn-excluir-tudo-notificacoes" onClick={handleClearAll}>
                {t("notifications_page.action_clear_all")}
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="notifications-list">
          {notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h2>{t("notifications_page.empty_title")}</h2>
              <p>
                {filter === "unread" && t("notifications_page.empty_unread")}
                {filter === "read" && t("notifications_page.empty_read")}
                {filter === "all" && t("notifications_page.empty_all")}
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div key={notification.id} className={`notification-card ${notification.read ? "read" : "unread"}`}>
                <div
                  className="notification-indicator"
                  style={{
                    backgroundColor: getNotificationColor(notification.type),
                  }}
                ></div>

                <div className="notification-icon-wrapper">
                  <div
                    className="notification-type-icon"
                    style={{
                      backgroundColor: `${getNotificationColor(notification.type)}20`,
                    }}
                  >
                    {notification.icon || getNotificationIcon(notification.type)}
                  </div>
                </div>

                <div className="notification-content">
                  <div className="notification-header-row">
                    <h3 className="notification-title">{notification.title}</h3>
                    <span className="notification-time">{notification.time}</span>
                  </div>
                  <p className="notification-message">{notification.message}</p>

                  {notification.bot && (
                    <Link to={`/bots/${notification.bot.id}`} className="notification-bot-link">
                      <img src={notification.bot.avatar} alt={notification.bot.name} className="bot-avatar-small" />
                      <span>{notification.bot.name}</span>
                    </Link>
                  )}
                </div>

                <div className="notification-actions">
                  {!notification.read && (
                    <button className="action-btn mark-read-btn" onClick={() => handleMarkAsRead(notification.id)} title={t("notifications_page.tooltips.mark_read")}>
                      ✓
                    </button>
                  )}
                  <button className="action-btn delete-btn" onClick={() => handleDelete(notification.id)} title={t("notifications_page.tooltips.delete")}>
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load More */}
        {pagination.hasMore && (
          <div className="load-more-section">
            <button className="btn btn-secondary" onClick={() => loadNotifications(pagination.offset + pagination.limit)}>
              {t("notifications_page.load_more")}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t("notifications_page.modals.delete_title")}</h2>
            </div>
            <div className="modal-body">
              <p>{t("notifications_page.modals.delete_body")}</p>
              <p className="modal-warning">{t("notifications_page.modals.delete_warning")}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                {t("actions.cancel")}
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                {t("actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearAllModal && (
        <div className="modal-overlay" onClick={cancelClearAll}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t("notifications_page.modals.clear_all_title")}</h2>
            </div>
            <div className="modal-body">
              <p>
                <Trans i18nKey="notifications_page.modals.clear_all_body">
                  Tem certeza que deseja excluir <strong>TODAS</strong> as suas notificações?
                </Trans>
              </p>
              <p className="modal-warning">
                <Trans i18nKey="notifications_page.modals.clear_all_warning" values={{ count: pagination.total }}>
                  ⚠️ Esta ação é <strong>IRREVERSÍVEL</strong>! Todas as {pagination.total} notificações serão permanentemente excluídas.
                </Trans>
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelClearAll}>
                {t("actions.cancel")}
              </button>
              <button className="btn btn-danger btn-confirmar-excluir-tudo" onClick={confirmClearAll}>
                {t("actions.yesClearAll")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{successMessage.includes("✅") ? t("notifications_page.modals.success_title") : t("notifications_page.modals.error_title")}</h2>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: "1.1rem", textAlign: "center" }}>{successMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowSuccessModal(false)}>
                {t("actions.ok")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
